import { Injectable } from '@angular/core';

/**
 * Icon Loader Service
 *
 * Ensures Material Icons font is loaded before the application starts rendering.
 * This prevents the "cutout" or placeholder icon issue.
 */
@Injectable({
  providedIn: 'root'
})
export class IconLoaderService {
  private fontsLoaded = false;
  private readonly TIMEOUT_MS = 5000;

  constructor() {
    void this.preloadIcons();
  }

  /**
   * Preload Material Icons font
   */
  private async preloadIcons(): Promise<void> {
    if (typeof document === 'undefined' || !document.fonts) {
      console.warn('Font Loading API not available');
      return;
    }

    try {
      // Wait for Material Icons font to load
      const fontLoadPromise = document.fonts.load('24px "Material Icons"');
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Font load timeout')), this.TIMEOUT_MS)
      );

      await Promise.race([fontLoadPromise, timeoutPromise]);

      this.fontsLoaded = true;

      // Verify the font is actually available
      this.verifyFontLoaded();
    } catch (error) {
      console.error('❌ Error loading Material Icons:', error);
      this.fallbackToAlternativeMethod();
    }
  }

  /**
   * Verify the font loaded correctly
   */
  private verifyFontLoaded(): void {
    const testDiv = document.createElement('div');
    testDiv.style.fontFamily = 'Material Icons';
    testDiv.textContent = 'check';
    testDiv.style.position = 'absolute';
    testDiv.style.left = '-9999px';
    testDiv.style.fontSize = '24px';
    document.body.appendChild(testDiv);

    setTimeout(() => {
      const width = testDiv.offsetWidth;
      document.body.removeChild(testDiv);

      if (width <= 0) {
        console.error('❌ Material Icons not rendering - font may not be loaded');
        this.showWarning();
      }
    }, 100);
  }

  /**
   * Fallback method if Font Loading API fails
   */
  private fallbackToAlternativeMethod(): void {
    // Check if font link is present
    const fontLink = document.querySelector('link[href*="fonts.googleapis.com"]');
    if (!fontLink) {
      console.error('❌ Material Icons font link not found in HTML');
      this.showWarning();
      return;
    }

    // Wait for document.fonts.ready
    if (document.fonts.ready) {
      document.fonts.ready
        .then(() => {
          this.fontsLoaded = true;
        })
        .catch((error) => {
          console.error('❌ Error in fallback font loading:', error);
          this.showWarning();
        });
    }
  }

  /**
   * Show warning to user if icons fail to load
   */
  private showWarning(): void {
    console.warn(`
╔════════════════════════════════════════════════════╗
║  ⚠️  MATERIAL ICONS NOT LOADED                     ║
║                                                    ║
║  Icons may appear as placeholders or boxes.       ║
║  Please check:                                     ║
║  1. Internet connection                            ║
║  2. Browser console for CSP errors                 ║
║  3. fonts.googleapis.com is not blocked            ║
╚════════════════════════════════════════════════════╝
    `);
  }

  /**
   * Check if fonts are loaded
   */
  public areFontsLoaded(): boolean {
    return this.fontsLoaded;
  }

  /**
   * Wait for fonts to be loaded
   */
  public async waitForFonts(): Promise<boolean> {
    const startTime = Date.now();

    while (!this.fontsLoaded && Date.now() - startTime < this.TIMEOUT_MS) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return this.fontsLoaded;
  }
}
